import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import {
  cilSpeedometer,
  cilPeople,
  cilDollar,
  cilLayers,
  cilSettings,
  cilShieldAlt,
  cilBell,
  cilCommentSquare,
  cilLightbulb,
  cilTag,
  cilBadge,
  cilPuzzle,
  cilUserFollow,
  cilUserUnfollow,
  cilInbox,
  cilChartLine,
  cilBarChart,
  cilBullhorn,
  cilWarning,
  cilTask,
  cilChartPie,
  cilBalanceScale,
} from '@coreui/icons'
import { AppSidebar as SharedAppSidebar, type RenderNavLink } from '@scryerlabs/ui'
import routePaths from '../../routes/route-paths'

/**
 * Routing stays here. The package takes a render prop rather than importing react-router,
 * so it pins no router version for its consumers.
 */
const renderNavLink: RenderNavLink = ({ href, className, end, children }) => (
  <NavLink to={href} end={end} className={className}>
    {children}
  </NavLink>
)
import { useAuthorization } from '../../context'

interface AppSidebarProps {
  visible: boolean
  onVisibleChange: (visible: boolean) => void
  unfoldable: boolean
  onUnfoldableChange: (unfoldable: boolean) => void
}

interface AuthzHelpers {
  isAdmin: boolean
  hasPermission: (permission: string) => boolean
}

interface NavItemConfig {
  key: string
  to: string
  icon: string | string[]
  labelKey: string
  visible?: (authz: AuthzHelpers) => boolean
}

interface NavSectionConfig {
  key: string
  titleKey?: string
  items: NavItemConfig[]
}

/**
 * Single source of truth for the sidebar. `visible` hides the menu entry only; the route is
 * still reachable by URL.
 */
const NAV_SECTIONS: NavSectionConfig[] = [
  {
    key: 'main',
    items: [
      {
        key: 'dashboard',
        to: routePaths.Dashboard,
        icon: cilSpeedometer,
        labelKey: 'common:navigation.dashboard',
      },
      {
        key: 'customers',
        to: routePaths.Customers,
        icon: cilPeople,
        labelKey: 'common:navigation.customers',
      },
    ],
  },
  {
    key: 'tradingStrategies',
    titleKey: 'common:navigation.tradingStrategiesSection',
    items: [
      {
        key: 'tradingStrategies',
        to: routePaths.TradingStrategies,
        icon: cilLayers,
        labelKey: 'common:navigation.tradingStrategies',
      },
      {
        key: 'customerFeedback',
        to: routePaths.CustomerFeedback,
        icon: cilCommentSquare,
        labelKey: 'common:navigation.customerFeedback',
      },
      {
        key: 'strategyRequests',
        to: routePaths.StrategyRequests,
        icon: cilLightbulb,
        labelKey: 'common:navigation.strategyRequests',
      },
      {
        key: 'symbols',
        to: routePaths.Symbols,
        icon: cilDollar,
        labelKey: 'common:navigation.symbols',
      },
      {
        key: 'brochureCatalogue',
        to: routePaths.BrochureCatalogue,
        icon: cilLayers,
        labelKey: 'common:navigation.brochureCatalogue',
        visible: (a) => a.isAdmin || a.hasPermission('brochure-catalogue:view'),
      },
      {
        key: 'portfolioBuilder',
        to: routePaths.PortfolioBuilder,
        icon: cilChartPie,
        labelKey: 'common:navigation.portfolioBuilder',
        visible: (a) => a.isAdmin || a.hasPermission('portfolio-builder-revisions:create'),
      },
      {
        key: 'portfolioTracker',
        to: routePaths.PortfolioTracker,
        icon: cilBalanceScale,
        labelKey: 'common:navigation.portfolioTracker',
        visible: (a) => a.isAdmin || a.hasPermission('portfolio-builder-revisions:create'),
      },
    ],
  },
  {
    key: 'incoming',
    titleKey: 'common:navigation.incomingSection',
    items: [
      {
        key: 'incomingSignals',
        to: routePaths.IncomingSignals,
        icon: cilInbox,
        labelKey: 'common:navigation.signals',
      },
      {
        key: 'strategyDataAlerts',
        to: routePaths.StrategyDataAlerts,
        icon: cilChartLine,
        labelKey: 'common:navigation.strategyData',
      },
      {
        key: 'infoAlerts',
        to: routePaths.InfoAlerts,
        icon: cilBullhorn,
        labelKey: 'common:navigation.infoAlerts',
      },
      {
        key: 'unknownAlerts',
        to: routePaths.UnknownAlerts,
        icon: cilWarning,
        labelKey: 'common:navigation.unknownAlerts',
      },
    ],
  },
  {
    key: 'selling',
    titleKey: 'common:navigation.selling',
    items: [
      {
        key: 'memberships',
        to: routePaths.MembershipPlatforms,
        icon: cilBadge,
        labelKey: 'common:navigation.memberships',
      },
      {
        key: 'addOns',
        to: routePaths.AddOns,
        icon: cilPuzzle,
        labelKey: 'common:navigation.addOns',
      },
      {
        key: 'offers',
        to: routePaths.Offers,
        icon: cilTag,
        labelKey: 'common:navigation.offers',
      },
    ],
  },
  {
    key: 'toDo',
    titleKey: 'common:navigation.toDo',
    items: [
      {
        key: 'accessToGrant',
        to: routePaths.AccessToGrant,
        icon: cilUserFollow,
        labelKey: 'common:navigation.accessToGrant',
        visible: (a) => a.hasPermission('tradingview-scripts:invite'),
      },
      {
        key: 'accessToRemove',
        to: routePaths.AccessToRemove,
        icon: cilUserUnfollow,
        labelKey: 'common:navigation.accessToRemove',
        visible: (a) => a.hasPermission('tradingview-scripts:remove'),
      },
      {
        key: 'membershipRequests',
        to: routePaths.MembershipRequests,
        icon: cilBadge,
        labelKey: 'common:navigation.membershipRequests',
        visible: (a) => a.hasPermission('customer-memberships:approve'),
      },
    ],
  },
  {
    key: 'admin',
    titleKey: 'common:navigation.admin',
    items: [
      {
        key: 'outstandingActionsDigest',
        to: routePaths.OutstandingActionsDigestRuns,
        icon: cilBell,
        labelKey: 'common:navigation.outstandingActionsDigest',
      },
      {
        key: 'marketCandles',
        to: routePaths.MarketCandleRuns,
        icon: cilBarChart,
        labelKey: 'common:navigation.marketCandles',
      },
      {
        key: 'permissions',
        to: routePaths.AdminPermissions,
        icon: cilShieldAlt,
        labelKey: 'common:navigation.permissions',
        visible: (a) => a.isAdmin,
      },
      {
        key: 'devTools',
        to: routePaths.DevTools,
        icon: cilSettings,
        labelKey: 'common:navigation.devTools',
        visible: (a) => a.hasPermission('dev-tools:wake-up'),
      },
      {
        key: 'processes',
        to: routePaths.Processes,
        icon: cilTask,
        labelKey: 'common:navigation.processes',
        visible: (a) => a.hasPermission('admin:run-processes'),
      },
    ],
  },
]

const AppSidebar: React.FC<AppSidebarProps> = ({
  visible,
  onVisibleChange,
  unfoldable,
  onUnfoldableChange,
}) => {
  const { t } = useTranslation()
  const { isAdmin, hasPermission } = useAuthorization()

  const authz: AuthzHelpers = { isAdmin, hasPermission }

  // Role filtering stays here, and only the survivors are handed over. The
  // package renders sections; it has no business knowing what a permission is.
  const sections = NAV_SECTIONS.flatMap((section) => {
    const items = section.items.filter((item) => (item.visible ? item.visible(authz) : true))
    if (items.length === 0) return []

    return [
      {
        key: section.key,
        title: section.titleKey ? t(section.titleKey) : undefined,
        items: items.map((item) => ({
          key: item.key,
          href: item.to,
          icon: item.icon,
          label: t(item.labelKey),
        })),
      },
    ]
  })

  return (
    <SharedAppSidebar
      sections={sections}
      renderNavLink={renderNavLink}
      visible={visible}
      onVisibleChange={onVisibleChange}
      unfoldable={unfoldable}
      onUnfoldableChange={onUnfoldableChange}
    />
  )
}

export default AppSidebar
